/**
 * MeetingV2DTO
 *
 * Part of the MeetingV2 re-architecture. This DTO corresponds to the
 * MeetingV2Blueprint in special_sauce (app/blueprints/one_on_ones/meeting_v2_blueprint.rb)
 */

/**
 * API response interface with camelCase keys.
 * Uses 'Key-Inflection': 'camel' header for automatic snake_case -> camelCase conversion.
 */

import { format } from 'date-fns';

/**
 * Structured summary types returned by the AI summarization pipeline.
 * These provide a richer, topic-organized view of the meeting content.
 */

export type QuoteSource = 'transcript' | 'written_notes';
export type WarningType = 'missing_transcript' | 'short_transcript' | 'missing_written_content';

export interface Quote {
	text: string;
	speaker: string;
	source: QuoteSource;
	timestamp: string | null;
}

export interface DiscussionPoint {
	id: string;
	summary: string;
	importance: 'high' | 'medium' | 'low';
	quotes: Quote[];
}

export interface SummaryTopic {
	id: string;
	title: string;
	category: 'career' | 'performance' | 'goals' | 'feedback' | 'collaboration' | 'challenges' | 'wins' | 'other';
	summary: string;
	discussionPoints: DiscussionPoint[];
}

/**
 * The actual summary content, present when generation succeeds.
 * Includes optional warning fields that can be set even on success.
 */
export interface StructuredMeetingSummaryData {
	meetingId: string;
	overallSummary: string;
	sentiment: 'positive' | 'neutral' | 'concerned';
	topics: SummaryTopic[];
	warningType: WarningType | null;
	warningMessage: string | null;
}

/**
 * API envelope for the structured summary, including generation status.
 * The summary data fields (meetingId, overallSummary, sentiment, topics)
 * are present at the same level when `success === true`.
 */
export interface StructuredSummaryResponse {
	startedAt: string;
	completedAt: string | null;
	success: boolean;
	error: string | null;
	warningType: WarningType | null;
	warningMessage: string | null;
	// Summary data fields — present when success === true
	meetingId?: string;
	overallSummary?: string;
	sentiment?: 'positive' | 'neutral' | 'concerned';
	topics?: SummaryTopic[];
}

/**
 * Full API response interface with all meeting fields.
 * Used for individual meeting detail pages.
 */
export interface MeetingV2APIData {
	id: string;
	title: string;
	status: string;
	participantIds: string[];
	participantKey: string;
	transcriptUserId: string | null;
	transcriptText: string | null;
	createdAt: string;
	updatedAt: string;
	completedAt: string | null;
	archivedAt: string | null;
	summarizationStartedAt: string | null;
	summarizationEndedAt: string | null;
	summarizedBrief: string | null;
	hasChatter: boolean;
	previousMeetingId: string | null;
	collaborativeDocumentId: string;
	collaborativeDocumentHtml: string | null;
	nextFoundMeetingAt: Date | null;
	botMeeting?: boolean;
	structuredSummary?: StructuredSummaryResponse | null;
	/** True when structured summary generation succeeded. If omitted, derived from structuredSummary.success */
	hasStructuredSummary?: boolean;
}

/**
 * Summarization state derived from backend fields.
 * Use getSummarizationState() for a single source of truth.
 */
export type SummarizationState = 'not_started' | 'generating' | 'enhancing' | 'structured_ready' | 'brief_only' | 'no_content';

export class MeetingV2DTO {
	static readonly STATUS_UPCOMING = 'upcoming';
	static readonly STATUS_COMPLETE = 'complete';

	id: string;
	title: string;
	status: string;
	participantIds: string[];
	participantKey: string;
	transcriptUserId: string | null;
	transcriptText: string | null;
	createdAt: Date;
	updatedAt: Date;
	completedAt: Date | null;
	archivedAt: Date | null;
	summarizationStartedAt: Date | null;
	summarizationEndedAt: Date | null;
	summarizedBrief: string | null;
	hasChatter: boolean;
	previousMeetingId: string | null;
	collaborativeDocumentId: string;
	collaborativeDocumentHtml: string | null;
	nextFoundMeetingAt: Date | null;
	botMeeting?: boolean;
	structuredSummary: StructuredSummaryResponse | null;
	hasStructuredSummary: boolean;

	apiData: MeetingV2APIData;

	/**
	 * Constructs a MeetingV2DTO from full API data.
	 * For list/index views, use MeetingV2ListDTO instead.
	 */
	constructor(data: MeetingV2APIData | MeetingV2DTO) {
		const apiData = data instanceof MeetingV2DTO ? data.apiData : data;
		this.apiData = apiData;

		this.id = apiData.id;
		this.title = apiData.title;
		this.status = apiData.status;
		this.participantIds = apiData.participantIds;
		this.participantKey = apiData.participantKey;
		this.transcriptUserId = apiData.transcriptUserId;
		this.transcriptText = apiData.transcriptText;
		this.createdAt = new Date(apiData.createdAt);
		this.updatedAt = new Date(apiData.updatedAt);
		this.completedAt = apiData.completedAt ? new Date(apiData.completedAt) : null;
		this.archivedAt = apiData.archivedAt ? new Date(apiData.archivedAt) : null;
		this.summarizationStartedAt = apiData.summarizationStartedAt ? new Date(apiData.summarizationStartedAt) : null;
		this.summarizationEndedAt = apiData.summarizationEndedAt ? new Date(apiData.summarizationEndedAt) : null;
		this.summarizedBrief = apiData.summarizedBrief;
		this.hasChatter = apiData.hasChatter;
		this.previousMeetingId = apiData.previousMeetingId;
		this.collaborativeDocumentId = apiData.collaborativeDocumentId;
		this.collaborativeDocumentHtml = apiData.collaborativeDocumentHtml;
		this.nextFoundMeetingAt = apiData.nextFoundMeetingAt ? new Date(apiData.nextFoundMeetingAt) : null;
		this.botMeeting = apiData.botMeeting ?? false;
		this.structuredSummary = apiData.structuredSummary ?? null;
		this.hasStructuredSummary = apiData.hasStructuredSummary ?? (!!apiData.structuredSummary && apiData.structuredSummary.success === true);
	}

	formatCompletedAt(formatString: string): string | null {
		return this.completedAt ? format(this.completedAt, formatString) : null;
	}

	/**
	 * Format the display date for this meeting.
	 * Uses completedAt for completed meetings, createdAt as fallback.
	 */
	formatDisplayDate(formatString: string): string {
		const date = this.completedAt ?? this.createdAt;
		return format(date, formatString);
	}

	isComplete(): boolean {
		return this.status === MeetingV2DTO.STATUS_COMPLETE;
	}

	isUpcoming(): boolean {
		return this.status === MeetingV2DTO.STATUS_UPCOMING;
	}

	/**
	 * Single source of truth for summary UI. Derives state from backend fields
	 * (summarizationStartedAt, summarizationEndedAt, summarizedBrief, hasStructuredSummary).
	 * Includes 15-minute stale safety: if summarization started but never ended, treat as done.
	 */
	getSummarizationState(): SummarizationState {
		const STALE_MS = 15 * 60 * 1000;
		const isStale = this.summarizationStartedAt && !this.summarizationEndedAt && Date.now() - this.summarizationStartedAt.getTime() > STALE_MS;
		const isInsufficientContent = this.structuredSummary?.success === false && this.structuredSummary?.error === 'insufficient_content';

		if (!this.summarizationStartedAt) return 'not_started';
		if (this.summarizationEndedAt || isStale || isInsufficientContent) {
			if (this.hasStructuredSummary) return 'structured_ready';
			if (this.summarizedBrief) return 'brief_only';
			return 'no_content';
		}
		if (this.summarizedBrief) return 'enhancing';
		return 'generating';
	}

	hasTranscript(): boolean {
		return !!this.transcriptText;
	}

	/**
	 * Returns true if transcript generation is in progress.
	 * This occurs when chatter (real-time transcription turns) exists but hasn't been processed into transcript_text yet.
	 */
	isTranscribing(): boolean {
		return this.hasChatter && !this.transcriptText;
	}

	isParticipant(userId: string): boolean {
		return this.participantIds.includes(userId);
	}

	isArchived(): boolean {
		return this.archivedAt !== null;
	}

	/**
	 * Returns the structured summary data if generation succeeded.
	 * Extracts the data fields from the status envelope into a clean object.
	 */
	getStructuredSummaryData(): StructuredMeetingSummaryData | null {
		const ss = this.structuredSummary;
		if (!ss?.success || !ss.topics) return null;
		return {
			meetingId: ss.meetingId ?? this.id,
			overallSummary: ss.overallSummary ?? '',
			sentiment: ss.sentiment ?? 'neutral',
			topics: ss.topics,
			warningType: ss.warningType,
			warningMessage: ss.warningMessage,
		};
	}

	/**
	 * Get display title for the meeting header.
	 * @param otherParticipantFirstName - The first name of the other participant
	 * @returns "1:1 with {firstName}" or falls back to the meeting title
	 */
	getDisplayTitle(otherParticipantFirstName?: string): string {
		if (otherParticipantFirstName) {
			return `1:1 with ${otherParticipantFirstName}`;
		}
		return this.title;
	}
}

/**
 * Lightweight API response for list/index views.
 * Only includes fields needed for displaying meeting cards.
 */
export interface MeetingV2ListData {
	id: string;
	title: string;
	status: string;
	participantIds: string[];
	participantKey: string;
	createdAt: string;
	updatedAt: string;
	completedAt: string | null;
	nextFoundMeetingAt: string | null;
	// Needed for IndexedDB persistence cleanup (useCleanupCompletedPersistence)
	collaborativeDocumentId: string | null;
	botMeeting?: boolean;
}

/**
 * Lightweight DTO for list/index views.
 * Contains only the fields needed for displaying meeting cards and cleanup.
 * Use MeetingV2DTO for detail pages that need full meeting data.
 */
export class MeetingV2ListDTO {
	static readonly STATUS_UPCOMING = 'upcoming';
	static readonly STATUS_COMPLETE = 'complete';

	id: string;
	title: string;
	status: string;
	participantIds: string[];
	participantKey: string;
	createdAt: Date;
	updatedAt: Date;
	completedAt: Date | null;
	nextFoundMeetingAt: Date | null;
	collaborativeDocumentId: string | null;
	botMeeting?: boolean;

	constructor(data: MeetingV2ListData) {
		this.id = data.id;
		this.title = data.title;
		this.status = data.status;
		this.participantIds = data.participantIds;
		this.participantKey = data.participantKey;
		this.createdAt = new Date(data.createdAt);
		this.updatedAt = new Date(data.updatedAt);
		this.completedAt = data.completedAt ? new Date(data.completedAt) : null;
		this.nextFoundMeetingAt = data.nextFoundMeetingAt ? new Date(data.nextFoundMeetingAt) : null;
		this.collaborativeDocumentId = data.collaborativeDocumentId;
		this.botMeeting = data.botMeeting ?? false;
	}

	/**
	 * Format the display date for this meeting.
	 * Uses completedAt for completed meetings, createdAt as fallback.
	 */
	formatDisplayDate(formatString: string): string {
		const date = this.completedAt ?? this.createdAt;
		return format(date, formatString);
	}

	isComplete(): boolean {
		return this.status === MeetingV2ListDTO.STATUS_COMPLETE;
	}

	isUpcoming(): boolean {
		return this.status === MeetingV2ListDTO.STATUS_UPCOMING;
	}
}
